import React from "react";
import { Survey, SurveyReport, SurveySegment, segmentListsMatch, UnscoredSelectionStats, segmentListIsInLists } from "../../../../../../amorg-common";
import { ActionPlanListView } from "../../../../../../amorg-common/DTOs/ActionPlanListView";
import { colors } from "../../../../../../designSystem/colors/color";
import { Flex } from "../../../../../../designSystem/compositeComponents/FlexHelpers";
import { RadialPctChart } from "../../../../../../designSystem/graphs/RadialPctChart";
import { Icon } from "../../../../../../designSystem/icons/Icons";
import { P, Span14 } from "../../../../../../designSystem/typography/Body";
import { PageSection } from "../../../../../../designSystem/PageSection";
import { ChipPanel, InfoChip } from "../../../../../../designSystem/Chip";
import { Card } from "../../../../../../designSystem/Card";

export function SurveyActionPlanStatusSection (props: { headerText: string, expandable?: boolean, survey: Survey, surveyReport: SurveyReport, actionPlans: ActionPlanListView[]; }): JSX.Element
{

    function getSegmentsWithoutPlans (surveyReport: SurveyReport, actionPlans: ActionPlanListView[])
    {
        const segmentsWithoutPlans: SurveySegment[][] = [];
        for (const cr of surveyReport.selectionReports)
        {
            let matched = false;
            for (const ap of actionPlans)
            {
                if (ap.type === 'WPA')
                {
                    for (const selectionList of ap.segmentFilter)
                    {
                        if (segmentListsMatch(selectionList, cr.segments))
                        {
                            matched = true;
                            break;
                        }
                    }
                    if (matched)
                        break;
                }
            }
            if (!matched)
                segmentsWithoutPlans.push(cr.segments);
        }
        return segmentsWithoutPlans;
    }

    const segmentsWithoutPlans = getSegmentsWithoutPlans(props.surveyReport, props.actionPlans);

    let completionPct = 0;
    if (props.survey)
    {
        const allSegmentedAnswers = props.surveyReport.selectionReports.map(sr =>
        ({
            segments: sr.segments,
            answerCount: sr.questionIndexedSummaries.length === 0
                ? 0
                : new UnscoredSelectionStats(sr.questionIndexedSummaries[0].selectionSummary).totalSelections
        })
        );

        const segmentsWithActionplans = allSegmentedAnswers.filter(sa =>
            !segmentListIsInLists(segmentsWithoutPlans, sa.segments)
        );

        const allAnswers = allSegmentedAnswers.reduce((acc, sa) => { return acc + sa.answerCount; }, 0);
        const answersWithActionplans = segmentsWithActionplans.reduce((acc, sa) => { return acc + sa.answerCount; }, 0);

        completionPct = answersWithActionplans / allAnswers * 100;
    }

    return (
        <div>
            <PageSection headerStyleVariant="card" contentStyleVariant="card"
                header={{ title: props.headerText, defaultCollapsed: false }}>

                <Flex gap={32}>

                    <Card variant="outlined" style={{ width: '50%' }}>
                        <Flex gap={16} alignItems="center">

                            <div style={{ flex: '0 0 140px' }}>
                                <RadialPctChart pct={completionPct} dim="140px" />
                            </div>

                            <Flex flexDirection="column" gap={32}>
                                <div>
                                    <Span14>
                                        {completionPct.toFixed(0)}% procent af spørgeskemaets besvarelser er omfattet af en eller flere handleplaner.
                                    </Span14>
                                </div>

                                {completionPct === 0 &&
                                    <>
                                        <div>
                                            <Span14>
                                                Da der endnu ikke er oprettet handleplaner for nogen af spørgeskemaets segmenteringer, viser grafen 0%.
                                            </Span14>
                                        </div>
                                    </>
                                }
                            </Flex>
                        </Flex>
                    </Card>


                    <Card variant="none" padding="0px" style={{ width: '50%' }}>

                        <Flex flexDirection="column" gap={16} justifyContent="flex-start" style={{height: '100%'}}>
                            {completionPct > 0 && completionPct < 100 &&
                                <Card variant="lightGray">
                                    <Flex gap={16} alignItems="center">
                                        <Icon name="exclamation" color={colors.icon.active} />
                                        <P>Der mangler fortsat at blive oprettet handleplaner for en eller flere af spørgeskemaets segmenteringer (svargrupper).</P>
                                    </Flex>
                                </Card>
                            }

                            {completionPct > 99.9 &&
                                <Card variant="lightGray">
                                    <Flex gap={16} alignItems="center">
                                        <Icon name="actions_done" color={colors.icon.success} />
                                        <P>Alle spørgeskemaets besvarelser er omfattet af handleplaner!</P>
                                    </Flex>
                                </Card>
                            }

                            {completionPct < 100 &&
                                <>
                                    <ChipPanel>
                                        {segmentsWithoutPlans.map((segments, index) =>
                                            <InfoChip key={index} variant="filled-light">
                                                {segments.map(s => s.value).join(' ')}
                                            </InfoChip>
                                        )}
                                    </ChipPanel>
                                </>
                            }
                        </Flex>

                    </Card>
                </Flex>

            </PageSection>
        </div>
    );
}